import { Effect, Match } from 'effect';

import { capabilityLayer } from '@vs-shop/engine/capability/capabilityLayer';
import { ProductListingError } from '@vs-shop/engine/capability/productListing/errors';
import type {
  ListingPageRequest,
  ListingRequest,
} from '@vs-shop/engine/capability/productListing/listingRequestKey';
import { scopeKeyOf } from '@vs-shop/engine/capability/productListing/listingScope';
import { ProductListing } from '@vs-shop/engine/capability/productListing/ProductListing';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { requestBrandExpandedById } from '@vs-shop/engine/request/backendApi/brand/getBrandExpandedById';
import { requestTaxonExpandByCode } from '@vs-shop/engine/request/backendApi/category/getTaxonExpandedByCode';
import { makeApiRequest } from '@vs-shop/engine/service/api/makeApiRequest';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { SearchFacets } from '@vs-shop/schemas/search/searchFacets';

import { BackendApi } from '../service/api/BackendApi';
import { requestListingProducts } from './listingProducts';
import { toProductPage } from './productListing';
import { toCategoryFacetSet, toSearchFacetSet } from './toProductListingFacets';
import { toProductListingQuery } from './toProductListingQuery';

const facetsPath = '/api/v2/shop/search/products/facets';
const facetsError = createBackendApiError({
  domain: 'Search',
  endpoint: 'ListingFacets',
  path: facetsPath,
  identifierField: 'scope',
});
const searchFacets = makeApiRequest(BackendApi)({
  path: facetsPath,
  method: 'get',
  accept: 'application/ld+json',
  schema: SearchFacets,
  errors: facetsError,
  module: ApiModule.SearchFacets,
});

const products = (asked: ListingPageRequest) =>
  requestListingProducts(asked).pipe(
    Effect.map((response) => toProductPage(response, asked)),
    Effect.mapError(ProductListingError.fromRequestFailure('products', scopeKeyOf(asked.scope)))
  );

const facets = (asked: ListingRequest) => {
  const reference = scopeKeyOf(asked.scope);
  const read = Match.value(asked.scope).pipe(
    Match.tag('Query', ({ text }) =>
      searchFacets(
        { query: { ...toProductListingQuery(asked.scope, asked.selection, false), inStock: true } },
        text
      ).pipe(Effect.map((response) => toSearchFacetSet(response, asked.selection)))
    ),
    Match.tag('Taxon', ({ code }) =>
      Effect.all(
        [
          requestTaxonExpandByCode(code),
          requestListingProducts({ ...asked, page: 1, itemsPerPage: 1 }),
        ],
        { concurrency: 'unbounded' }
      ).pipe(
        Effect.map(([taxon, page]) =>
          toCategoryFacetSet(taxon, asked.selection, page['hydra:totalItems'])
        )
      )
    ),
    Match.tag('Brand', ({ id }) =>
      Effect.all(
        [
          requestBrandExpandedById(id),
          requestListingProducts({ ...asked, page: 1, itemsPerPage: 1 }),
        ],
        { concurrency: 'unbounded' }
      ).pipe(
        Effect.map(([brand, page]) =>
          toCategoryFacetSet(brand, asked.selection, page['hydra:totalItems'])
        )
      )
    ),
    Match.exhaustive
  );

  return read.pipe(Effect.mapError(ProductListingError.fromRequestFailure('facets', reference)));
};

export const ProductListingLive = capabilityLayer(ProductListing, { products, facets });
